import type { FC, FormEvent, ReactNode } from 'react'
import type { GetServerSideProps } from 'next'

import Link from 'next/link'
import { useRouter } from 'next/router'
import { useState } from 'react'
import { Loader2 } from 'lucide-react'
import { api, errorMessage } from '@/lib/client/api'
import { Button } from '@/components/ui/button'
import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { AuthShell } from '@/components/auth/auth-shell'
import { FormError, FormField } from '@/components/auth/form-field'
import { safeNext } from '@/lib/navigation'
import { withGuest } from '@/lib/server/auth'

const Login: FC = (): ReactNode => {
    const router = useRouter()
    const next = safeNext(router.query.next, '/posts')
    const [email, setEmail] = useState('')
    const [password, setPassword] = useState('')
    const [error, setError] = useState<string | null>(null)
    const [busy, setBusy] = useState(false)

    const submit = async (event: FormEvent): Promise<void> => {
        event.preventDefault()
        setBusy(true)
        setError(null)

        try {
            await api('/api/auth/login', { method: 'POST', body: { email, password } })
            await router.replace(next)
        }
        catch (caught) {
            setError(errorMessage(caught))
            setBusy(false)
        }
    }

    const signupHref = next === '/posts' ? '/signup' : `/signup?next=${encodeURIComponent(next)}`

    return (
        <Layout variant='bare'>
            <Seo title='Log in' noIndex />

            <AuthShell
                title='Welcome back'
                subtitle='Log in to keep working on your posts and feeds.'
                footer={(
                    <p>
                        New to Feedsmith?{' '}
                        <Link href={signupHref} className='font-medium text-foreground underline underline-offset-4'>
                            Create an account
                        </Link>
                    </p>
                )}
            >
                <form onSubmit={submit} className='flex flex-col gap-5' noValidate>
                    <FormField id='email' label='Email' type='email' autoComplete='email' required value={email} onChange={event => setEmail(event.target.value)} />

                    <FormField
                        id='password'
                        label='Password'
                        type='password'
                        autoComplete='current-password'
                        required
                        value={password}
                        onChange={event => setPassword(event.target.value)}
                        hint={(
                            <Link href='/forgot-password' className='text-sm text-muted-foreground underline-offset-4 hover:text-foreground hover:underline'>
                                Forgot password?
                            </Link>
                        )}
                    />

                    <FormError message={error} />

                    <Button type='submit' disabled={busy} className='h-11 rounded-full text-base'>
                        {busy && <Loader2 className='size-4 animate-spin' />}
                        Log in
                    </Button>
                </form>
            </AuthShell>
        </Layout>
    )
}

export const getServerSideProps: GetServerSideProps = async ctx => withGuest(ctx, '/posts')

export default Login
